/**
 * [INPUT]: 依赖生成的 DTO 和浏览器 fetch；请求地址、凭证和错误响应由调用方提供
 * [OUTPUT]: 对外提供 API 类型、含幂等灵感消费的 ApiClient 和统一 ApiClientError
 * [POS]: site-web/admin-web 的 HTTP 适配层，隔离组件与后端 snake_case 传输细节；存档 UUID、当前 revision 与幂等写入在此统一归一化
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */
export * from './generated';
export * from './auth';

import type {
  AIRunInput,
  AIEvent,
  ApiError,
  GameCoverVariants,
  GameSummary,
  InspirationBalance,
  SaveEnvelope,
  UserSession,
} from './generated';
import type { components } from './openapi';

type GameDTO = components['schemas']['Game'];

function normalizeGame(game: GameDTO): GameSummary {
  const status: GameSummary['status'] = game.status === 'published'
    ? 'published'
    : game.status === 'unpublished'
      ? 'unpublished'
      : 'draft';
  return {
    id: game.id,
    slug: game.slug,
    title: game.title,
    description: game.description,
    category: game.category,
    tags: game.tags ?? [],
    coverUrl: game.cover_url || undefined,
    coverVariants: normalizeCoverVariants(game.cover_variants),
    status,
    runtimeMinVersion: game.runtime_min_version,
    version: game.version ?? '',
    capabilities: game.capabilities ?? [],
    saveSchemaVersion: game.save_schema_version ?? 0,
    publishedAt: game.published_at,
    createdAt: game.created_at,
    updatedAt: game.updated_at,
  };
}

function normalizeCoverVariants(raw: GameDTO['cover_variants']): GameCoverVariants | undefined {
  if (!raw) return undefined;
  const result: GameCoverVariants = {};
  for (const slot of ['hero', 'card', 'thumb'] as const) {
    const value = raw[slot];
    if (!value) continue;
    result[slot] = { assetId: value.asset_id, url: value.url, ratio: value.ratio };
  }
  return Object.keys(result).length ? result : undefined;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly payload?: ApiError,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export type ApiClientOptions = {
  baseUrl: string;
  fetcher?: typeof fetch;
  credentials?: RequestCredentials;
  accessToken?: () => string | null | undefined;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function normalizeSaveEnvelope(value: unknown): SaveEnvelope | null {
  const raw = asRecord(value);
  const slotKey = raw.slot_key ?? raw.slotKey;
  const gameVersion = raw.game_version ?? raw.gameVersion;
  const schemaVersion = raw.schema_version ?? raw.schemaVersion;
  const revision = raw.revision;
  const baseRevision = revision ?? raw.base_revision ?? raw.baseRevision;
  const summary = asRecord(raw.summary);
  const state = asRecord(raw.state);
  if (
    typeof slotKey !== 'string'
    || typeof gameVersion !== 'string'
    || typeof schemaVersion !== 'number'
    || typeof baseRevision !== 'number'
    || !raw.summary
    || !raw.state
  ) return null;

  const slotId = raw.slot_id ?? raw.slotId;
  return {
    ...(typeof slotId === 'string' ? { slotId } : {}),
    slotKey,
    // 下一次保存必须基于服务端当前 revision，而不是该 revision 创建时的旧基线。
    baseRevision,
    gameVersion,
    schemaVersion,
    summary,
    state,
  };
}

function createIdempotencyKey(): string {
  const randomUUID = globalThis.crypto?.randomUUID?.bind(globalThis.crypto);
  return randomUUID ? randomUUID() : `save-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export class ApiClient {
  private readonly fetcher: typeof fetch;

  constructor(private readonly options: ApiClientOptions) {
    this.fetcher = options.fetcher ?? fetch;
  }

  private headers(init?: HeadersInit, accept = 'application/json'): Headers {
    const headers = new Headers(init);
    headers.set('accept', accept);
    const accessToken = this.options.accessToken?.();
    if (accessToken && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${accessToken}`);
    return headers;
  }

  async getMe(): Promise<UserSession> {
    return this.request<UserSession>('/me');
  }

  async listGames(): Promise<GameSummary[]> {
    const response = await this.request<{ games?: GameDTO[]; items?: GameDTO[] }>('/games');
    return (response.games ?? response.items ?? []).map(normalizeGame);
  }

  async getInspiration(): Promise<InspirationBalance> {
    return this.request<InspirationBalance>('/me/inspiration');
  }

  async consumeInspiration(amount: number, reference: string, reason: string): Promise<InspirationBalance> {
    return this.request<InspirationBalance>('/me/inspiration/consumptions', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ amount, reference, reason }),
    });
  }

  async getSave(gameId: string, slotKey: string): Promise<SaveEnvelope | null> {
    const response = await this.request<unknown>(
      `/me/games/${encodeURIComponent(gameId)}/saves/${encodeURIComponent(slotKey)}`,
    );
    const direct = normalizeSaveEnvelope(response);
    if (direct) return direct;
    return normalizeSaveEnvelope(asRecord(response).save);
  }

  async putSave(gameId: string, slotKey: string, save: SaveEnvelope): Promise<SaveEnvelope> {
    const idempotencyKey = createIdempotencyKey();
    const response = await this.request<unknown>(
      `/me/games/${encodeURIComponent(gameId)}/saves/${encodeURIComponent(slotKey)}`,
      {
        method: 'PUT',
        headers: {
          'content-type': 'application/json',
          'if-match': String(save.baseRevision),
          'x-idempotency-key': idempotencyKey,
        },
        body: JSON.stringify({
          ...(save.slotId ? { slot_id: save.slotId } : {}),
          base_revision: save.baseRevision,
          game_version: save.gameVersion,
          schema_version: save.schemaVersion,
          summary: save.summary,
          state: save.state,
          idempotency_key: idempotencyKey,
        }),
      },
    );
    const direct = normalizeSaveEnvelope(response);
    if (direct) return direct;
    const wrapped = asRecord(response);
    const revision = normalizeSaveEnvelope(wrapped.revision) ?? normalizeSaveEnvelope(wrapped.save);
    if (revision) return revision;
    throw new ApiClientError('保存响应格式不正确。', 502);
  }

  async *runAI(input: AIRunInput): AsyncIterable<AIEvent> {
    const response = await this.fetcher(`${this.options.baseUrl}/games/${input.gameId}/ai/runs`, {
      method: 'POST',
      cache: 'no-store',
      credentials: this.options.credentials ?? 'omit',
      headers: this.headers({
        'content-type': 'application/json',
        'idempotency-key': input.idempotencyKey,
      }, 'text/event-stream'),
      body: JSON.stringify({
        ...(input.promptId ? { prompt_id: input.promptId } : {}),
        ...(input.promptMode ? { prompt_mode: input.promptMode } : {}),
        prompt_input: input.prompt,
        ...(input.promptContext ? { prompt_context: input.promptContext } : {}),
        ...(input.responseFormat ? { response_format: { type: input.responseFormat } } : {}),
      }),
    });

    if (!response.ok || !response.body) {
      throw await this.toError(response);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';
      for (const frame of frames) {
        const data = frame
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trim())
          .join('');
        if (data) yield JSON.parse(data) as AIEvent;
      }
    }
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await this.fetcher(`${this.options.baseUrl}${path}`, {
      ...init,
      cache: 'no-store',
      credentials: this.options.credentials ?? 'omit',
      headers: this.headers(init?.headers),
    });
    if (!response.ok) throw await this.toError(response);
    return (await response.json()) as T;
  }

  private async toError(response: Response): Promise<ApiClientError> {
    let payload: ApiError | undefined;
    try {
      payload = (await response.json()) as ApiError;
    } catch {
      // 非 JSON 错误使用状态码兜底，避免吞掉网络上下文。
    }
    return new ApiClientError(payload?.message ?? `Request failed: ${response.status}`, response.status, payload);
  }
}
